// packages/core/src/learning/schools/career-capital.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored exhibit for "Career capital". Matches boglehead-indexing.ts gold
// standard.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — held [] for the post-authoring cross-ref
//                     pass. Worldview/exhibit names only: ramit-sethi.
//                     NOTE: SchoolMeta thinker is Cal Newport, who appears in
//                     canon as an author (not a figures-tally entry per the
//                     protocol). 'cal-newport' is NOT currently a FigureId in
//                     figureRegistry.ts — do not add to figures until/unless
//                     that id exists.
//   - applyActions:   ApplyAction union — type-checked at compile time. Only
//                     live/allowed action types emitted.
//   - featuredLessonIds / debateIds — [] until the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const CAREER_CAPITAL_EXHIBIT: SchoolExhibitEntry = {
	id: 'career-capital',
	tagline: 'Career capital believes skills become future options.',
	worldviewSummary:
		'Career capital is paying attention to the invisible assets you build through skill, proof, trust, reputation, and judgment. This lens protects you from treating a job as only a paycheck instead of a place where future leverage can be built. It accepts a real tradeoff: building valuable career capital can require patience, difficult work, feedback, and seasons where growth matters more than comfort. The blind spot is that career capital can become status chasing if it forgets health, values, family, and the fact that not every workplace rewards people fairly. At its best, this lens turns work into a way to build options, not just survive another pay period.',
	coreBeliefs: [
		'Career capital believes skill is an asset that can travel with you.',
		'Career capital believes reputation can open doors before money does.',
		'Career capital believes proof matters more than vague potential.',
		'Career capital believes hard projects can become future bargaining power.',
		'Career capital believes income growth is often built before it is paid.',
		'Career capital believes career moves should build options, not only status.',
	],
	warnsAgainst: [
		'Treating every job as only a paycheck with no future signal.',
		'Chasing status while failing to build real skill, proof, or trust.',
		'Staying comfortable so long that your options quietly shrink.',
		'Assuming hard work will be rewarded automatically without visibility, negotiation, or market awareness.',
	],
	helps: 'This lens helps people who want to raise their earning power, become harder to replace, and create more career options over time. It may not fit when the pursuit of skill and status starts damaging health, relationships, or self respect.',
	figures: [],
	canon: [
		{
			title: 'So Good They Cannot Ignore You',
			author: 'Cal Newport',
			oneLineIdea:
				'Rare and valuable skills can create more control over work over time.',
		},
		{
			title: 'Deep Work',
			author: 'Cal Newport',
			oneLineIdea:
				'Focused effort on hard things can build skill that shallow busyness cannot replace.',
		},
		{
			title: 'I Will Teach You To Be Rich',
			author: 'Ramit Sethi',
			oneLineIdea:
				'Earning power, negotiation, and career moves can be major money levers.',
		},
		{
			title: 'Proof of work',
			oneLineIdea:
				'Visible results often speak louder than saying you are capable.',
		},
		{
			title: 'Reputation capital',
			oneLineIdea:
				'Trust, reliability, and judgment can become assets that follow you across roles.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-career-capital',
	debateIds: [],
	applyActions: [
		{
			label: 'Check whether your current money picture depends too much on one job',
			action: {
				type: 'openDashboard',
				label: 'Open your Dashboard',
			},
		},
		{
			label: 'Test how higher income could change your options',
			action: {
				type: 'openSimulator',
				label: 'Simulate higher income',
				scenarioType: 'side-hustle',
			},
		},
		{
			label: 'Check how much runway gives you career breathing room',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'Name one skill that could become bargaining power',
			action: {
				type: 'noToolAction',
				label: 'Name your skill asset',
				prompt: 'Write one skill, project, or reputation signal that could make you more valuable in the next twelve months.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Career capital asks a direct question: what are you building that travels with you?',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of comfort is waking up with fewer options.',
			kind: 'hook',
		},
		{
			line: 'What skill changed your income more than any budget ever did?',
			kind: 'interview-question',
		},
		{
			line: 'Ask someone what project became the proof that changed their career.',
			kind: 'creator-prompt',
		},
		{
			line: 'Nisk helps users learn career capital, then test how income changes their options.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience why the best raise is often built before it is negotiated.',
			kind: 'creator-prompt',
		},
		{
			line: 'Where does career capital create freedom, and where does it become status chasing?',
			kind: 'interview-question',
		},
		{
			line: 'A paycheck pays today. Career capital can change tomorrow.',
			kind: 'hook',
		},
	],
	revision: 1,
};
